import { usersApi } from "../store/api/usersApi";
import { runMutation, runQuery } from "../store/api/runtime";
import type { LocationSnapshot } from "../types/location";
import type { UserProfileAchievementsViewModel } from "../types/view-models";

export interface PublicUserSummary {
  id: string;
  username: string | null;
  name: string | null;
  avatar: string | null;
  verified: boolean;
  trustScore: string | number | null;
  reviewCount: number;
}

export interface PublicUserProfile extends PublicUserSummary {
  itemsListed: number;
  salesCount: number;
  createdAt?: string | null;
}

export async function searchPublicUsers(params: { q: string; limit?: number }): Promise<PublicUserSummary[]> {
  return runQuery(usersApi.endpoints.searchPublicUsers.initiate, params);
}

export async function fetchUserById(id: string): Promise<unknown> {
  return runQuery(usersApi.endpoints.fetchUserById.initiate, id);
}

export async function fetchPublicUserById(id: string): Promise<unknown> {
  return runQuery(usersApi.endpoints.fetchPublicUserById.initiate, id);
}

export async function fetchPublicUserByUsername(username: string): Promise<PublicUserProfile> {
  return runQuery(usersApi.endpoints.fetchPublicUserByUsername.initiate, username);
}

export async function fetchUserAchievements(id: string): Promise<UserProfileAchievementsViewModel> {
  return runQuery(usersApi.endpoints.fetchUserAchievements.initiate, id);
}

export async function updateUserAvatar(userId: string, file: Blob | File): Promise<unknown> {
  return runMutation(usersApi.endpoints.updateUserAvatar.initiate, { userId, file });
}

export async function updateUserLocation(userId: string, location: string): Promise<unknown> {
  return runMutation(usersApi.endpoints.updateUserLocation.initiate, { userId, location });
}

export async function updateProfileLocation(
  userId: string,
  location: LocationSnapshot,
): Promise<unknown> {
  return runMutation(usersApi.endpoints.updateProfileLocation.initiate, { userId, location });
}

export async function updateBrowsingLocation(
  userId: string,
  location: LocationSnapshot,
  radiusMiles?: number,
): Promise<unknown> {
  return runMutation(usersApi.endpoints.updateBrowsingLocation.initiate, {
    userId,
    location,
    radiusMiles,
  });
}

export async function resetBrowsingLocation(userId: string, radiusMiles?: number): Promise<unknown> {
  return runMutation(usersApi.endpoints.resetBrowsingLocation.initiate, { userId, radiusMiles });
}

export async function relocateUser(
  userId: string,
  payload: {
    profileLocation: LocationSnapshot;
    mode: "move_all" | "keep_current" | "select_posts";
    selection?: {
      tradeIds?: string[];
      bountyIds?: string[];
      listingIds?: string[];
    };
  },
): Promise<unknown> {
  return runMutation(usersApi.endpoints.relocateUser.initiate, { userId, payload });
}
